import React, { createContext, useContext, useState, ReactNode } from "react";
import { 
    CustomerInterface,
    ProductInterface,
} from "../pages/OrderDetailsPage/OrderDetailsInterface";
import { PaymentDetailsInterface } from "../components/cards/PaymentDetailsInterface";
import { ShippingAddressInterface } from "../components/cards/ShippingAddressInterface";
import { FulfilmentStateInterface } from "../components/cards/FulfilmentStateInterface";
import { Order } from "../pages/SalesPage/SalesPageInterface";
import { LogisticState } from "../types/order/logisticState";
import { OrderDetailsInterface } from "../pages/OrderDetailsPage/OrderDetailsInterface";

interface OrderDetailsContextType {
    orderDetails: Order | null;
    customer: CustomerInterface | undefined;
    products: ProductInterface[];
    paymentDetails: PaymentDetailsInterface | undefined;
    shippingAddress: ShippingAddressInterface | undefined;
    fulfillmentState: FulfilmentStateInterface | undefined;
    loading: boolean;
    error: string | null;
    setOrderDetailsData: (data: OrderDetailsInterface) => void;
    setLoadingState: (isLoading: boolean) => void;
    setErrorState: (error: string | null) => void;
    changeOrderFulfillmentState: (fulfillmentState: FulfillmentState) => void;
}

type FulfillmentState = "unfulfilled" | "fulfilled";

export const OrderDetailsContext = createContext<OrderDetailsContextType | undefined>(undefined);

export const useOrderDetails = () => {
    const context = useContext(OrderDetailsContext);
    if (context === undefined) {
        throw new Error("useOrderDetails must be used within an OrderDetailsProvider");
    }
    return context;
};

interface OrderDetailsProviderProps {
    children: ReactNode;
    hashId: string | undefined;
}

export const OrderDetailsProvider: React.FC<OrderDetailsProviderProps> = ({ 
    children,
}) => {
    const [orderDetails, setOrderDetails] = useState<Order | null>(null);
    const [customer, setCustomer] = useState<CustomerInterface>();
    const [products, setProducts] = useState<ProductInterface[]>([]);
    const [paymentDetails, setPaymentDetails] = useState<PaymentDetailsInterface>();
    const [shippingAddress, setShippingAddress] = useState<ShippingAddressInterface>();
    const [fulfillmentState] = useState<FulfilmentStateInterface>();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const setOrderDetailsData = (data: OrderDetailsInterface) => {
        setOrderDetails(data.order);
        // data.order.dropi_state = DropiState.SUCCESS;
        // data.order.dropi_state = DropiState.ERROR;
        // data.order.dropi_url = "https://stg.dropi.co/dashboard.orders";
        setCustomer(data.customer);
        setProducts(data.products);
        
        // CARDS
        const paymentDetails: PaymentDetailsInterface = {
            subtotal: data.order.total_value_with_discount,
            total: data.order.total_value_with_discount,
            paymentDate: data.order.created_at,
            paymentMethod: data.order.payment_method,
            paymentState: data.order.payment_state,
            products: data.products,
            benefitPurchases: data.order.benefit_purchases,
        };
        setPaymentDetails(paymentDetails);

        const shippingAddress: ShippingAddressInterface = {
            name: data.customer.name,
            last_name: data.customer.last_name,
            address: data.customer.address,
            address_detail: data.customer.address_detail,
            city: data.customer.city,
            department: data.customer.department,
            neighborhood: data.customer.neighborhood,
        };
        setShippingAddress(shippingAddress);
    };

    const setLoadingState = (isLoading: boolean) => {
        setLoading(isLoading);
    };

    const setErrorState = (errorMsg: string | null) => {
        setError(errorMsg);
    };

    const changeOrderFulfillmentState = (fulfillmentState: FulfillmentState) => {
        setOrderDetails(prevOrderDetails => {
            if (prevOrderDetails) {
                return {
                    ...prevOrderDetails,
                    logistic_state: fulfillmentState === "fulfilled" ? LogisticState.IN_PROGRESS : LogisticState.CREATED
                };
            }
            return prevOrderDetails;
        });
    };

    const value = {
        orderDetails,
        customer,
        products,
        paymentDetails,
        shippingAddress,
        fulfillmentState,
        loading,
        error,
        setOrderDetailsData,
        setLoadingState,
        setErrorState,
        changeOrderFulfillmentState,
    };

    return (
        <OrderDetailsContext.Provider value={value}>
            {children}
        </OrderDetailsContext.Provider>
    );
};
